import {
  VerificationAppDocumentType,
  TVerificationAppElement,
} from "../../../../app/models";
import { TDocument, TDocumentData } from "../../../models/document";
import {
  DOCUMENT_TYPE_FIELDS,
} from "../../BinaryContent/fieldDefinitions";
import { titleCase } from "../../../../../utils";
import { PdfSection, PdfRow, PdfAttachment } from "./types";
import { DefaultSectionContext, buildDefaultSection } from "./defaultSectionBuilder";
import { serializeField, serializeReport } from "./fieldSerializers";
import { parseLinkedVrs } from "../../../utils/linkedVrs";
import { getEntityRepresentativeRows } from "../../../utils/entityRepresentatives";

type SectionExporter = (
  document: TDocument,
  stepConfig: TVerificationAppElement | null,
  context: DefaultSectionContext
) => PdfSection;

function prependDocumentMetadataRows(section: PdfSection): void {
  const info = section.document?.document;
  if (!info) return;
  const prepend: PdfRow[] = [];
  if (info.declineReason.length > 0) {
    prepend.push({
      label: "Decline reason(s)",
      value: info.declineReason.join(", "),
    });
  }
  if (info.warnings.length > 0) {
    prepend.push({
      label: "Warning(s)",
      value: info.warnings.join(", "),
    });
  }
  section.rows.unshift(...prepend);
}

function splitBinaryAndFiles(document: TDocument): {
  binary: TDocumentData[];
  files: TDocumentData[];
} {
  const binary: TDocumentData[] = [];
  const files: TDocumentData[] = [];
  for (const item of document.data) {
    if (item.type === "binary") binary.push(item);
    else if (item.type === "file") files.push(item);
  }
  return { binary, files };
}

function collectRemainingAttachments(
  document: TDocument,
  context: DefaultSectionContext,
  attachmentsFromFields: PdfAttachment[],
  files: TDocumentData[]
): PdfAttachment[] {
  const matchedFileIds = new Set(attachmentsFromFields.map((a) => a.file.id));
  const remainingFiles = files.filter((f) => !matchedFileIds.has(f.id));
  return [
    ...attachmentsFromFields,
    ...remainingFiles.map((file) => ({ label: file.dataId, file })),
  ];
}

function addCommentRow(
  rows: PdfRow[],
  document: TDocument,
  context: DefaultSectionContext
): void {
  const comment = context.comments[document.document.documentId];
  if (comment) {
    rows.push({ label: "Comment", value: comment });
  }
}

function buildIdentificationSection(
  document: TDocument,
  stepConfig: TVerificationAppElement | null,
  context: DefaultSectionContext
): PdfSection {
  const { binary, files } = splitBinaryAndFiles(document);
  const documentType = document.document.documentType;

  const reportEntries = binary.filter(
    (d) =>
      d.dataId === "IdentificationReport" || d.dataId === "ScreeningReport"
  );
  const mainEntry = binary.find((d) => d.dataId === documentType);
  const fields = DOCUMENT_TYPE_FIELDS[documentType];

  const rows: PdfRow[] = [];
  for (const report of reportEntries) {
    rows.push(...serializeReport(report));
  }

  const attachmentsFromFields: PdfAttachment[] = [];
  if (mainEntry && fields?.length) {
    const data = (mainEntry.data ?? {}) as Record<string, unknown>;
    for (const field of fields) {
      const result = serializeField(
        field,
        data,
        files,
        context.countriesRegex
      );
      rows.push(...result.rows);
      attachmentsFromFields.push(...result.attachments);
    }
  }

  addCommentRow(rows, document, context);
  const attachments = collectRemainingAttachments(
    document,
    context,
    attachmentsFromFields,
    files
  );

  return {
    title: titleCase(documentType),
    document,
    rows,
    attachments,
  };
}

function buildSelfieSection(
  document: TDocument,
  _stepConfig: TVerificationAppElement | null,
  context: DefaultSectionContext
): PdfSection {
  const { binary, files } = splitBinaryAndFiles(document);

  const reportEntries = binary.filter(
    (d) =>
      d.dataId === "IdentificationReport" || d.dataId === "ScreeningReport"
  );

  const rows: PdfRow[] = [];
  for (const report of reportEntries) {
    rows.push(...serializeReport(report));
  }

  addCommentRow(rows, document, context);
  const attachments = files.map((file) => ({ label: file.dataId, file }));

  return {
    title: "Selfie",
    document,
    rows,
    attachments,
  };
}

function buildUserInformationSection(
  document: TDocument,
  stepConfig: TVerificationAppElement | null,
  context: DefaultSectionContext
): PdfSection {
  const { binary, files } = splitBinaryAndFiles(document);
  const documentType = document.document.documentType;
  const mainEntry = binary.find((d) => d.dataId === documentType);

  const rows: PdfRow[] = [];
  if (context.userInfo) {
    if (context.userInfo.phone != null && context.userInfo.phone !== "") {
      rows.push({ label: "Phone number", value: context.userInfo.phone });
    }
    if (context.userInfo.email != null && context.userInfo.email !== "") {
      rows.push({ label: "Email", value: context.userInfo.email });
    }
    if (
      context.userInfo.externalEmail != null &&
      context.userInfo.externalEmail !== ""
    ) {
      rows.push({
        label: "External system email",
        value: context.userInfo.externalEmail,
      });
    }
  }

  const attachmentsFromFields: PdfAttachment[] = [];
  if (mainEntry && stepConfig?.fields?.length) {
    const data = (mainEntry.data ?? {}) as Record<string, unknown>;
    for (const field of stepConfig.fields) {
      const result = serializeField(
        field,
        data,
        files,
        context.countriesRegex
      );
      rows.push(...result.rows);
      attachmentsFromFields.push(...result.attachments);
    }
  }

  addCommentRow(rows, document, context);
  const attachments = collectRemainingAttachments(
    document,
    context,
    attachmentsFromFields,
    files
  );

  return {
    title: titleCase(documentType),
    document,
    rows,
    attachments,
  };
}

function buildBeneficialOwnershipSection(
  document: TDocument,
  stepConfig: TVerificationAppElement | null,
  context: DefaultSectionContext
): PdfSection {
  const { binary, files } = splitBinaryAndFiles(document);
  const mainEntry = binary.find(
    (d) => d.dataId === document.document.documentType
  );

  const rows: PdfRow[] = [];
  const attachmentsFromFields: PdfAttachment[] = [];

  if (mainEntry && stepConfig?.fields?.length) {
    const data = (mainEntry.data ?? {}) as Record<string, unknown>;
    for (const field of stepConfig.fields) {
      const result = serializeField(
        field,
        data,
        files,
        context.countriesRegex
      );
      rows.push(...result.rows);
      attachmentsFromFields.push(...result.attachments);
    }
  }

  if (context.companyPurpose != null && context.companyPurpose !== "") {
    rows.push({ label: "Use of Funds", value: "" });
    rows.push({
      label: "Purpose of establishing the business relationship",
      value: context.companyPurpose,
    });
  }

  addCommentRow(rows, document, context);
  const attachments = collectRemainingAttachments(
    document,
    context,
    attachmentsFromFields,
    files
  );

  return {
    title: titleCase(document.document.documentType),
    document,
    rows,
    attachments,
  };
}

function buildEntityRepresentativesSection(
  document: TDocument,
  _stepConfig: TVerificationAppElement | null,
  context: DefaultSectionContext
): PdfSection {
  const { binary, files } = splitBinaryAndFiles(document);
  const mainEntry = binary.find(
    (d) => d.dataId === document.document.documentType
  );

  const rows: PdfRow[] = [];
  const attachmentsFromFields: PdfAttachment[] = [];

  if (!mainEntry) {
    addCommentRow(rows, document, context);
    const attachments = files.map((file) => ({ label: file.dataId, file }));
    return {
      title: titleCase(document.document.documentType),
      document,
      rows,
      attachments,
    };
  }

  const linkedVrs = parseLinkedVrs(document.data);
  const personRows = getEntityRepresentativeRows(mainEntry, linkedVrs);

  rows.push(
    ...personRows
      .filter((person) => !person.isRemoved)
      .map((person) => {
      const roles = [
        person.representative ? "Representative" : "",
        person.ubo ? "UBO" : "",
      ].filter(Boolean);
      const valueParts = [`Roles: ${roles.length > 0 ? roles.join(", ") : "None"}`];

      if (person.isSelfService && person.contact) {
        valueParts.push(`Contact: ${person.contact}`);
      }

      return {
        label: person.fullName,
        value: valueParts.join("; "),
      };
    })
  );

  addCommentRow(rows, document, context);
  const attachments = collectRemainingAttachments(
    document,
    context,
    attachmentsFromFields,
    files
  );

  return {
    title: titleCase(document.document.documentType),
    document,
    rows,
    attachments,
  };
}

const EXPORTER_REGISTRY: Record<string, SectionExporter> = {
  Passport: buildIdentificationSection,
  Id: buildIdentificationSection,
  Selfie: buildSelfieSection,
  UserInformation: buildUserInformationSection,
  BeneficialOwnership: buildBeneficialOwnershipSection,
  EntityRepresentatives: buildEntityRepresentativesSection,
};

const STEP_EXPORTER_REGISTRY: Partial<
  Record<VerificationAppDocumentType, SectionExporter>
> = {
  UserInformation: buildUserInformationSection,
  BeneficialOwnership: buildBeneficialOwnershipSection,
  EntityRepresentatives: buildEntityRepresentativesSection,
};

/**
 * Builds a PdfSection for a document by dispatching to the appropriate document-type exporter, or default.
 */
export function buildSectionForDocument(
  document: TDocument,
  stepConfig: TVerificationAppElement | null,
  context: DefaultSectionContext
): PdfSection {
  const stepExporter = stepConfig
    ? STEP_EXPORTER_REGISTRY[stepConfig.type]
    : undefined;
  const documentExporter = EXPORTER_REGISTRY[document.document.documentType];
  const exporter = stepExporter ?? documentExporter;
  const section = exporter
    ? exporter(document, stepConfig, context)
    : buildDefaultSection(document, stepConfig, context);
  prependDocumentMetadataRows(section);
  return section;
}
